<!-- Maintain_MisconductCodes.vue -->
<template>
  <!-- Header / actions -->
  <v-toolbar flat density="comfortable" class="mt-4">
    <v-toolbar-title>ADMIN Misconduct Codes</v-toolbar-title>
    <v-spacer />
    <v-btn color="secondary" variant="tonal" prepend-icon="mdi-google-spreadsheet" @click="loadGoogleSheets_listMisconductData">
      Load Google Sheets data
    </v-btn>
    <v-btn color="primary" class="ml-2" prepend-icon="mdi-plus" @click="openCreate">
      Add Misconduct Item
    </v-btn>
  </v-toolbar>

  <!-- Filters -->
  <v-container fluid class="pt-2">
    <v-card rounded="lg" elevation="2" class="mb-3">
      <v-card-text>
        <v-row dense class="items-end">
          <v-col cols="12" sm="4" md="1">
            <v-select label="Group" v-model="groupFilter" :items="uniqueGroups" clearable density="comfortable" hide-details="auto"/></v-col>
          <v-col cols="12" sm="4" md="2">
            <v-select label="Offender" v-model="offenderFilter" :items="uniqueOffenders" clearable density="comfortable" hide-details="auto"/></v-col>
          <v-col cols="12" sm="4" md="2">
            <v-select label="Incident Type" v-model="incidentTypeFilter" :items="incidentTypes" clearable density="comfortable" hide-details="auto"/></v-col>
            <v-col cols="12" sm="4" md="2">
            <v-select label="Referee Action" v-model="refereeactionFilter" :items="refActionOptions" clearable density="comfortable"  hide-details="auto"/></v-col>
          <v-col cols="12" sm="12" md="3" class="d-flex ga-2">
            <v-btn variant="tonal" @click="setSortKey('SortOrder_IncidentType')">
              Sort: Incident Type
            </v-btn>
            <v-btn variant="tonal" @click="setSortKey('SortOrder_MisconductItem')">
              Sort: Misconduct Item
            </v-btn>
            <v-btn variant="text" @click="clearFilters">
              Clear
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card rounded="lg" elevation="2">
      <v-data-table
        :headers="headers"
        :items="filteredMisconductList"
        item-key="id"
        density="compact"
        class="elevation-0"
        fixed-header
        height="70vh"
        :items-per-page="-1"
        hide-default-footer
        @click:row="onRowClick"
      >
        <template v-slot:[`item.MisconductItem`]="{ item }">
          <div class="text-left">{{ item.MisconductItem }}</div>
        </template>
        <template v-slot:[`item.RefAction`]="{ item }">
          <v-chip
            :color="chipColor(item.RefAction)"
            :text-color="chipTextColor(item.RefAction)"
            size="small"
            label
          >
            {{ item.RefAction }}
          </v-chip>
        </template>
        <template v-slot:[`item.SortOrder_IncidentType`]="{ item }">
          <div class="text-left">{{ toNumber(item.SortOrder_IncidentType) }}</div>
        </template>
        <template v-slot:[`item.SortOrder_MisconductItem`]="{ item }">
          <div class="text-left">{{ toNumber(item.SortOrder_MisconductItem) }}</div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex ga-1">
            <v-btn icon="mdi-pencil" size="small" variant="text" @click.stop="openEdit(item)" />
            <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click.stop="deleteMiscItem(item.id)" />
          </div>
        </template>
        <template #no-data>
          <div class="py-8 text-medium-emphasis">No misconduct data available</div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>

  <!-- Add/Edit dialog -->
  <v-dialog v-model="dialog.open" max-width="900" scrollable>
    <v-card>
      <v-card-title class="text-h6">{{ dialog.isEdit ? 'Edit' : 'Add' }} Misconduct Item</v-card-title>
      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-3">
          <div class="text-subtitle-2 mb-1">Misconduct Summary:</div>
          <div class="font-mono text-body-2">{{ misconductSummary }}</div>
        </v-alert>

        <v-form ref="formRef" v-model="valid" @submit.prevent>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-select label="Group" v-model="form.Group" :items="['IFAB','CIF','PC']" :rules="[req]" density="comfortable" hide-details="auto"/></v-col>
            <v-col cols="12" sm="8">
              <v-select label="Offender" v-model="form.Offender" :items="offenderOptions"
                :rules="[req]" density="comfortable" hide-details="auto" /></v-col>
            <v-col cols="12" sm="6">
              <v-select label="Incident Type" v-model="form.IncidentType" :items="incidentTypes" :rules="[req]"
                density="comfortable" hide-details="auto" clearable /></v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Incident Type Description"
                v-model="form.IncidentTypeDescription"
                density="comfortable"
                hide-details="auto"
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field label="Misconduct Item" v-model="form.MisconductItem"
                :rules="[req]" density="comfortable" hide-details="auto" clearable/></v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                label="Misconduct Code"
                v-model="form.MisconductCode"
                density="comfortable"
                hide-details="auto"
                clearable
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field label="CIF Code" v-model="form.CIFCode"
                density="comfortable" hide-details="auto" clearable/></v-col>
            <v-col cols="12" sm="3">
              <v-text-field label="Law Reference (optional)" v-model="form.LawReference"
                density="comfortable" hide-details="auto" clearable/></v-col>
            <v-col cols="12" sm="3">
              <v-select label="Referee Action" v-model="form.RefAction" :items="refActionOptions"
                density="comfortable" hide-details="auto" clearable /></v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                label="SortOrder IncidentType"
                v-model="form.SortOrder_IncidentType"
                type="number"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                label="SortOrder MisconductItem"
                v-model="form.SortOrder_MisconductItem"
                type="number"
                density="comfortable"
                hide-details="auto"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                label="Applies To Incident (optional)"
                v-model="form.MisconductAppliesToIncident"
                density="comfortable"
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveMiscItem">
          {{ dialog.isEdit ? 'Save Changes' : 'Add Misconduct Item' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import axios from "axios"
import { ref, computed, onMounted } from "vue"
import { db, listMisconductColRef } from "@/firebase"
import { addDoc, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore"
import { getAuth } from "firebase/auth"
import { useRouter } from "vue-router"

// ---------------- state ----------------
const router = useRouter()
const saving = ref(false)
const valid = ref(false)
const formRef = ref(null)

const MisconductList = ref([])

const groupFilter = ref("")
const offenderFilter = ref("")
const incidentTypeFilter = ref("")
const refereeactionFilter = ref("")
const selectedSortKey = ref("SortOrder_IncidentType") // default

const dialog = ref({ open: false, isEdit: false })
const form = ref(resetForm())

// ---------------- constants ----------------
const offenderOptions = ['Player', 'Substituted Player', 'Team Official', 'Coach', 'Spectator', 'Substitute/Substituted Player']
const refActionOptions = ['Yellow Card', 'Red Card', 'WARN', 'REMOVAL']

const headers = [
  { title: "Group", key: "Group", width: 90 },
  { title: "Offender", key: "Offender", width: 150 },
  { title: "IncidentType", key: "IncidentType", width: 240 },
  { title: "MisconductCode", key: "MisconductCode", width: 120 },
  { title: "CIFCode", key: "CIFCode", width: 90 },
  { title: "MisconductItem", key: "MisconductItem", width: 420 },
  { title: "Referee Action", key: "RefAction", width: 160 },
  { title: "SortOrder IncidentType", key: "SortOrder_IncidentType", width: 120 },
  { title: "SortOrder MisconductItem", key: "SortOrder_MisconductItem", width: 140 },
  { title: "Actions", key: "actions", sortable: false, width: 100 },
]

// ---------------- lifecycle ----------------
onMounted(async () => {
  const auth = getAuth()
  if (!auth.currentUser) {
    router.push("/login")
    return
  }
  await loadMisconductList()
  await loadIncidentTypes()
})

// ---------------- helpers ----------------
const req = (v) => (v !== null && v !== undefined && String(v).trim() !== "") || "Required"
const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function resetForm() {
  return {
    id: "",
    Group: "",
    Offender: "",
    IncidentType: "",
    IncidentTypeDescription: "",
    MisconductAppliesToIncident: "",
    RefAction: "",
    MisconductItem: "",
    MisconductCode: "",
    CIFCode: "",
    SortOrder_IncidentType: "",
    SortOrder_MisconductItem: "",
    SortOrder_MisconudctItem: "",
    LawReference: "",
  }
}

const misconductSummary = computed(() => {
  const m = form.value
  if (!m) return ""
  return `[${m.Group}] ${m.IncidentType} (${m.Offender}) ${m.MisconductItem} -- ${m.MisconductCode} -- ${m.RefAction}`
})

const uniqueGroups = computed(() =>
  Array.from(new Set(MisconductList.value.map(i => i.Group).filter(Boolean))).sort()
)
const uniqueOffenders = computed(() =>
  Array.from(new Set(MisconductList.value.map(i => i.Offender).filter(Boolean))).sort()
)

// Custom action order: RC > DPC > PC > YC (you can tweak)
const actionOrder = new Map([
  ['Yellow Card', 0],  
  ['Red Card', 1],
  ['WARNING', 2],
  ['REMOVAL', 3],
  
])

function chipColor(a) {
  if (a === 'Yellow Card') return 'yellow'
  if (a === 'Red Card') return 'red'
  if (a === 'WARNING') return 'deep-purple-lighten-3'
  if (a === 'REMOVAL') return 'deep-purple-darken-3'
  return 'grey'
}
function chipTextColor(a) {
  if (a === 'Yellow Card') return 'black'
  return undefined
}

function setSortKey(k) {
  selectedSortKey.value = k
}
function clearFilters() {
  groupFilter.value = ""
  offenderFilter.value = ""
  incidentTypeFilter.value = ""
  refereeactionFilter.value = ""
}

// ---------------- data loads ----------------
async function loadMisconductList() {
  const snap = await getDocs(listMisconductColRef)
  MisconductList.value = snap.docs.map(d => {
    const x = d.data() || {}
    // normalize numeric sort fields; keep legacy typo as fallback
    const soi  = toNumber(x.SortOrder_IncidentType)
    const somi = toNumber(x.SortOrder_MisconductItem ?? x.SortOrder_MisconudctItem)
    return {
      id: d.id,
      Group: x.Group || '',
      Offender: x.Offender || '',
      IncidentType: x.IncidentType || '',
      IncidentTypeDescription: x.IncidentTypeDescription || '',
      MisconductAppliesToIncident: x.MisconductAppliesToIncident || '',
      RefAction: x.RefAction || '',
      MisconductItem: x.MisconductItem || '',
      MisconductCode: x.MisconductCode || '',
      CIFCode: x.CIFCode || '',
      SortOrder_IncidentType: soi,
      SortOrder_MisconductItem: somi,
      // keep original typo too in memory in case you want to see it
      SortOrder_MisconductItem: x.SortOrder_MisconductItem ?? '',
      LawReference: x.LawReference || '',
    }
  })
}

const incidentTypes = ref([])
async function loadIncidentTypes() {
  if (!MisconductList.value.length) return
  // derive from list
  const set = new Set()
  MisconductList.value.forEach(x => { if (x.IncidentType) set.add(x.IncidentType) })
  incidentTypes.value = Array.from(set).sort()
}

// ---------------- filtering + sorting ----------------
const filteredMisconductList = computed(() => {
  let list = MisconductList.value

  if (groupFilter.value) {
    const v = String(groupFilter.value).toLowerCase()
    list = list.filter(x => String(x.Group || '').toLowerCase() === v)
  }
  if (offenderFilter.value) {
    const v = String(offenderFilter.value).toLowerCase()
    list = list.filter(x => String(x.Offender || '').toLowerCase() === v)
  }
  if (incidentTypeFilter.value) {
    const v = String(incidentTypeFilter.value).toLowerCase()
    list = list.filter(x => String(x.IncidentType || '').toLowerCase() === v)
  }
  if (refereeactionFilter.value) {
    list = list.filter(x => x.RefAction === refereeactionFilter.value)
  }

  const key = selectedSortKey.value
  return [...list].sort((a, b) => {
    const ao = (actionOrder.get(a.RefAction) ?? 99)
    const bo = (actionOrder.get(b.RefAction) ?? 99)
    if (ao !== bo) return ao - bo

    const av = toNumber(a[key])
    const bv = toNumber(b[key])
    if (av !== bv) return av - bv

    // tie-breakers
    const ati = toNumber(a.SortOrder_IncidentType)
    const bti = toNumber(b.SortOrder_IncidentType)
    if (ati !== bti) return ati - bti

    const ami = toNumber(a.SortOrder_MisconductItem)
    const bmi = toNumber(b.SortOrder_MisconductItem)
    if (ami !== bmi) return ami - bmi

    return String(a.MisconductItem || '').localeCompare(String(b.MisconductItem || ''))
  })
})

// ---------------- row actions ----------------
function onRowClick(event, { item }) {
  openEdit(item)
}

function openCreate() {
  dialog.value = { open: true, isEdit: false }
  form.value = resetForm()
}

function openEdit(row) {
  dialog.value = { open: true, isEdit: true }
  form.value = {
    ...resetForm(),
    ...row,
    // ensure both normalized numeric
    SortOrder_IncidentType: toNumber(row.SortOrder_IncidentType),
    SortOrder_MisconductItem: toNumber(row.SortOrder_MisconductItem),
    SortOrder_MisconductItem: toNumber(row.SortOrder_MisconductItem), // keep typo sync
    LawReference: row.LawReference || "",
  }
}

function closeDialog() {
  dialog.value.open = false
}

// ---------------- save/delete ----------------
async function saveMiscItem() {
  const res = await formRef.value?.validate()
  if (!res?.valid) return

  saving.value = true
  try {
    const payload = {
      Group: (form.value.Group || '').trim(),
      Offender: (form.value.Offender || '').trim(),
      IncidentType: (form.value.IncidentType || '').trim(),
      IncidentTypeDescription: (form.value.IncidentTypeDescription || '').trim(),
      MisconductAppliesToIncident: String(form.value.MisconductAppliesToIncident || '').trim(),
      RefAction: String(form.value.RefAction || '').trim(),
      MisconductItem: String(form.value.MisconductItem || '').trim(),
      MisconductCode: (form.value.MisconductCode || '').trim(),
      CIFCode: (form.value.CIFCode || '').trim(),
      SortOrder_IncidentType: toNumber(form.value.SortOrder_IncidentType),
      SortOrder_MisconductItem: toNumber(form.value.SortOrder_MisconductItem),
      // write legacy typo as well for back-compat
      SortOrder_MisconudctItem: toNumber(form.value.SortOrder_MisconductItem),
      LawReference: (form.value.LawReference || '').trim(),
    }

    if (dialog.value.isEdit && form.value.id) {
      await updateDoc(doc(db, "listMisconduct", form.value.id), payload)
    } else {
      await addDoc(listMisconductColRef, payload)
    }

    await loadMisconductList()
    await loadIncidentTypes()
    closeDialog()
  } catch (e) {
    console.error("saveMiscItem", e)
  } finally {
    saving.value = false
  }
}

async function deleteMiscItem(id) {
  if (!confirm("Delete this misconduct item?")) return
  try {
    await deleteDoc(doc(db, "listMisconduct", id))
    await loadMisconductList()
    await loadIncidentTypes()
  } catch (e) {
    console.error("deleteMiscItem", e)
  }
}

// ---------------- Google Sheets loader (as-is) ----------------
async function loadGoogleSheets_listMisconductData() {
  const apiKey = 'YOUR_GOOGLE_SHEETS_API_KEY'
  const sheetId = '1KM3FvFxJJMLmYMAjOdsiW43CocroXocaer4dp4mIv5w'
  const range = 'listMisconduct!A2:K132'
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetId}/values/${range}?key=${apiKey}`

  try {
    const { data } = await axios.get(url)
    const rows = data?.values || []
    for (const row of rows) {
      const [
        Group, Offender, IncidentType, IncidentTypeDescription,
        MisconductAppliesToIncident, MisconductCode, CIFCode,
        MisconductItem, RefAction, SortOrder_IncidentType, SortOrder_MisconductItem
      ] = row

      const docData = {
        Group: Group || '',
        Offender: Offender || '',
        IncidentType: IncidentType || '',
        IncidentTypeDescription: IncidentTypeDescription || '',
        MisconductAppliesToIncident: MisconductAppliesToIncident || '',
        MisconductCode: MisconductCode || '',
        CIFCode: CIFCode || '',
        MisconductItem: MisconductItem || '',
        RefAction: RefAction || '',
        SortOrder_IncidentType: toNumber(SortOrder_IncidentType),
        SortOrder_MisconductItem: toNumber(SortOrder_MisconductItem),
        // keep legacy field in sync
        SortOrder_MisconudctItem: toNumber(SortOrder_MisconductItem),
      }
      await addDoc(listMisconductColRef, docData)
    }
    await loadMisconductList()
    await loadIncidentTypes()
  } catch (e) {
    console.error('Error fetching Google Sheets data:', e)
  }
}
</script>

<style scoped>
.text-left { text-align: left; }
</style>
